import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import YFManagerPopIn from './YFManagerPopIn'
import { IntlShape, useIntl } from 'react-intl'
import YFMobilePopIn from './YFMobilePopIn'
import fetchFactory from '../../utils/fetchFactory'
import { YfManagerDataModel, YfManagerImageModel, YfManagerModel } from '../../types/yfPopin/yfManagerModel'
import errorCatcher from '../../utils/errorCatcher'
import { RootState } from '../reducers'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../styles'
import { saveYFManagerData, saveYFManagerImage } from '../reducers/yfPopin/actions'
import { convertYfManagerData } from './YfPopinMapper'
import { defaultYfDisplay, typeYfManagerEnum } from '../../enums/yfManagerEnums'
import { ContractsModel } from '../../types/contracts/contractsModel'
import localStorageEnum from '../../enums/localStorageEnum'

const YF_MANAGER_ERROR = 'yf_manager'

const useStyle = makeStyles(
    (theme) => (
        {
            ...styles(theme),
            popinThumbnail: {
                height: 48,
                width: 48,
                borderRadius: 50
            },
            requestThumbnail: {
                borderRadius: 50
            }
        }
    )
)

interface Store {
    yfManager: YfManagerModel,
    contracts: ContractsModel[]
    errorCatcher: (error: any, id: string) => void,
    saveYFManagerImage: (yfManagerData: YfManagerImageModel) => void,
    saveYFManagerData: (yfManagerData: YfManagerDataModel) => void
}

type YFPopInProps = Store

const YFPopIn: React.FC<YFPopInProps> = (
    {
        yfManager,
        contracts,
        errorCatcher,
        saveYFManagerImage,
        saveYFManagerData
    }
) => {
    const classes = useStyle()
    const intl: IntlShape = useIntl()
    const isLargeScreen = useMediaQuery(
        {
            query: '(min-width: 770px)'
        }
    )

    const isYfManagerEmpty = (yfManager: YfManagerModel) => {
        return (!yfManager.request_thumbnail || yfManager.request_thumbnail.length === 0) &&
            (!yfManager.popin_thumbnail || yfManager.popin_thumbnail.length === 0) &&
            (!yfManager.picture || yfManager.picture.length === 0)
    }

    useEffect(
        () => {
            if (isYfManagerEmpty(yfManager)) {
                fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/information/getYFManager`)
                    .then(
                        (yfManagerResponse: any) => {
                            let tmpYfManager: YfManagerImageModel
                            if (Array.isArray(yfManagerResponse)) {
                                tmpYfManager = {
                                    popin_thumbnail: defaultYfDisplay,
                                    request_thumbnail: defaultYfDisplay,
                                    picture: defaultYfDisplay
                                }
                            } else {
                                tmpYfManager = {
                                    popin_thumbnail: yfManagerResponse.vignette_youfirst_manager && yfManagerResponse.vignette_youfirst_manager.length > 0 ?
                                        yfManagerResponse.vignette_youfirst_manager
                                        : defaultYfDisplay,
                                    request_thumbnail: yfManagerResponse.vignette_youfirst_manager && yfManagerResponse.vignette_youfirst_manager.length > 0 ?
                                        yfManagerResponse.vignette_youfirst_manager
                                        : defaultYfDisplay,
                                    picture: yfManagerResponse.photo_youfirst_manager && yfManagerResponse.photo_youfirst_manager.length > 0 ?
                                        yfManagerResponse.photo_youfirst_manager
                                        : defaultYfDisplay
                                }
                            }

                            saveYFManagerImage(tmpYfManager)
                        }
                    )
                    .catch(
                        (error: any) => {
                            errorCatcher(error, YF_MANAGER_ERROR)
                        }
                    )
            }

            const mainBuilding = localStorage.getItem(localStorageEnum.mainBuilding)
            // Sans bâtiment principal (valeur absente ou « null »), aucune donnée YFM à récupérer
            if ((!yfManager.data || !yfManager.data.nom) && mainBuilding && mainBuilding !== 'null') {
                fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/bien/getYfmData?mainBuilding=${mainBuilding}`)
                    .then((response: any) => {
                        if (response) {
                            saveYFManagerData(convertYfManagerData(response, intl))
                        }
                    })
                    .catch(
                        (error: any) => {
                            errorCatcher(error, YF_MANAGER_ERROR)
                        }
                    )
            }
        }, [classes.popinThumbnail, classes.requestThumbnail, errorCatcher, intl.locale, saveYFManagerImage, saveYFManagerData, yfManager, contracts, intl]
    )

    const whichTitle = () => {
        return intl.formatMessage({
            id: 'you_first_manager.listenDefault',
            defaultMessage: 'Nous sommes à votre écoute',
            description: 'Title message'
        })
    }

    const whichSubTitle = () => {
        switch (yfManager.data.type) {
            case typeYfManagerEnum.couple:
                return intl.formatMessage({
                    id: 'you_first_manager.managers',
                    defaultMessage: 'Vos YouFirst Managers',
                    description: 'Subtitle message'
                })
            case typeYfManagerEnum.manager:
                return intl.formatMessage({
                    id: 'you_first_manager.manager',
                    defaultMessage: 'Votre YouFirst Manager',
                    description: 'Subtitle message'
                })
            default:
                return intl.formatMessage({
                    id: 'you_first_manager.manager',
                    defaultMessage: 'Votre YouFirst Manager',
                    description: 'Subtitle message'
                })
        }
    }

    const whichUrgenceLabel = () => {
        return intl.formatMessage({
            id: 'you_first_manager.availabilityManager',
            defaultMessage: 'Numéros d\'urgence à utiliser en dehors des horaires d\'ouverture du bureau d\'accueil.',
            description: 'Urgence label'
        })
    }

    if (!yfManager.data || !yfManager.data.nom) {
        return <></>
    }

    return (
        <>
            {
                isLargeScreen ?
                    <YFManagerPopIn
                        subTitle={whichSubTitle()}
                        managerTitle={whichTitle()}
                        urgenceLabel={whichUrgenceLabel()}
                        youfirstManager={yfManager}
                    />
                    :
                    <YFMobilePopIn
                        subTitle={whichSubTitle()}
                        managerTitle={whichTitle()}
                        urgenceLabel={whichUrgenceLabel()}
                        youfirstManager={yfManager}
                    />
            }
        </>

    )
}

const mapStateToProps = (state: RootState) => (
    {
        yfManager: state.yfManager,
        contracts: state.contracts
    }
)

const mapDispatchToProps = {
    errorCatcher,
    saveYFManagerImage,
    saveYFManagerData
}

export default connect(mapStateToProps, mapDispatchToProps)(YFPopIn)